// Lib
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { isEmpty, isEqual } from 'lodash';
import { ContentState, convertFromRaw, convertToRaw } from 'draft-js';
import classNames from 'classnames';

// Components
import TableCellEditor from '../../../components/editor/tableCell/TableCellEditor';

// Utils
import {
    getCellTextAlignment,
    getLocalisedContent,
    getCellEditingAlignment,
} from '../utils/dataUtils/cell/tableCellDisplayUtils';
import { getBackgroundColorClass, getCellLevelTextStyles } from '../utils/dataUtils/cell/tableCellStyleUtils';
import { getTableCellVerticalAlignmentClassName } from '../utils/domUtils/tableCellVerticalAlignmentClassNameUtils';
import applyInlineStylesThroughoutContent from '../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import { getTableCellEditorId } from '../utils/tableEditorSessionUtils';
import { getHotTableCellData } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';

// Constants
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { CellTypeNames, TextAlignment } from '../../../../common/table/CellTypeConstants';

const MilanoteCellEditorComponent = (props) => {
    const {
        element,
        elementId,
        isEditable,
        currentlyEditingCellEditorCoords,
        currentlyEditingEditorId,
        hotTableContainerRef,
        hotTableInstanceRef,
        cellEditorRef,
        locale,
    } = props;

    const cellData = getHotTableCellData(hotTableInstanceRef.current, currentlyEditingCellEditorCoords);
    const verticalAlignment = cellData?.verticalAlignment;
    const cellTextStyle = cellData?.textStyle;

    const [textAlignment, setTextAlignment] = useState(null);
    const [textStyle, setTextStyle] = useState(null);

    // Set initial text alignment and styles
    useEffect(() => {
        if (!currentlyEditingCellEditorCoords) return;

        setTextStyle(Array.from(cellTextStyle || []));

        // For the editor only, we want to ensure that date types are right aligned
        // Even if the initial value is text, the datePicker will be opened with the current date
        const isDateTimeString = !isFormula(cellData?.value) && cellData?.type?.name === CellTypeNames.DATE_TIME;
        const editorTextAlignment = isDateTimeString ? TextAlignment.RIGHT : getCellTextAlignment(cellData, locale);

        setTextAlignment(editorTextAlignment);
    }, [currentlyEditingCellEditorCoords, locale]);

    /**
     * Get text content to be rendered in the cell editor.
     */
    const textContent = useMemo(() => {
        // IMPORTANT: Only update the textContent when user starts editing. This is to prevent a bug where text from
        // previously edited cell is still present in the cell editor when editing a different empty cell.
        // - MilanoteEditor has some optimisations that only updates its internal editorState state when isCurrentlyEditingTableCell
        //   is toggled.
        if (!currentlyEditingCellEditorCoords) return;
        if (!cellData) return null;

        let contentState = null;
        if (!!cellData.draftContent && !isFormula(cellData.value)) {
            contentState = convertFromRaw(cellData.draftContent);
        } else {
            if (isFormula(cellData.value)) return cellData.value;

            const localisedContent = getLocalisedContent(cellData, locale);

            contentState = ContentState.createFromText(localisedContent.toString());
        }

        // Apply cell-level text styling if needed
        if (!isEmpty(cellTextStyle)) {
            contentState = applyInlineStylesThroughoutContent(contentState, cellTextStyle);
        }

        return convertToRaw(contentState);
    }, [currentlyEditingCellEditorCoords, locale]);

    const backgroundClasses = useMemo(() => getBackgroundColorClass(cellData), [currentlyEditingCellEditorCoords]);

    const onChange = useCallback(
        ({ editorState }) => {
            if (!currentlyEditingCellEditorCoords) return;

            const currentContent = editorState.getCurrentContent();
            const editCellValue = currentContent.getPlainText();

            // check whether alignment should be updated
            const updateAlignment = getCellEditingAlignment(editCellValue, cellData, locale);
            if (updateAlignment) setTextAlignment(updateAlignment);

            // update the text style if the user has changed the inline styles
            if (editCellValue.length > 0) {
                const cellLevelTextStyles = currentContent ? getCellLevelTextStyles(currentContent) : [];
                if (!isEqual(cellLevelTextStyles.sort(), textStyle?.sort() || [])) {
                    setTextStyle(cellLevelTextStyles);
                }
            }

            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', editorState);
        },
        [currentlyEditingCellEditorCoords, cellData, locale, textStyle],
    );

    const portalTarget = hotTableInstanceRef.current?.rootElement || hotTableContainerRef.current;

    if (!portalTarget) return null;

    return createPortal(
        <div className={classNames('MilanoteCellEditorComponent', backgroundClasses)}>
            <div className="MilanoteCellEditorComponent-relative">
                <div className="handsontable-input-positioner">
                    <div
                        className={classNames(
                            'handsontable-input-holder',
                            getTableCellVerticalAlignmentClassName(verticalAlignment),
                        )}
                    >
                        <TableCellEditor
                            editorRef={cellEditorRef}
                            className="handsontable-input"
                            element={element}
                            isEditing={!!currentlyEditingCellEditorCoords}
                            isEditable={isEditable}
                            textContent={textContent}
                            defaultStyleOverride={textStyle}
                            textAlignment={textAlignment}
                            editorId={getTableCellEditorId(elementId)}
                            editorKey={TABLE_CELL_EDITOR_KEY}
                            currentEditorId={currentlyEditingEditorId}
                            onChange={onChange}
                        />
                    </div>
                </div>
            </div>
        </div>,
        portalTarget,
    );
};

MilanoteCellEditorComponent.propTypes = {
    element: PropTypes.object,
    elementId: PropTypes.string,
    isEditable: PropTypes.bool,
    currentlyEditingCellEditorCoords: PropTypes.object,
    currentlyEditingEditorId: PropTypes.string,
    currentlyEditingEditorKey: PropTypes.string,
    textContent: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    locale: PropTypes.string,

    hotTableContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    cellEditorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export default MilanoteCellEditorComponent;
